import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useTelegram } from '@/hooks/useTelegram';

/**
 * Одна фраза Славы на Статистике — про связь между едой и результатом.
 *
 * ЗАЧЕМ (ревизия Статистики 05.09.2026). Здесь больше всего данных
 * во всём приложении — и здесь Слава молчал. Графики лежат отдельно:
 * калории сами по себе, вес сам по себе, а связать их человек должен сам.
 * Одна честная фраза стоит больше, чем ещё один график.
 *
 * ⚠️ Считается на клиенте из того, что на экране УЖЕ загружено, без модели:
 * экран открывают часто, и вызов модели ради строки — деньги и ожидание
 * на ровном месте (тот же принцип, что в SlavaLine на главной).
 *
 * По хартии ненавязчивости: молчит, когда данных мало или сказать нечего.
 * Врать «ты молодец» при трёх записях — хуже, чем промолчать.
 */

type Day = { date: string; calories: number; protein: number };
type Weight = { date: string; w: number; raw: string };

export function buildStatsPhrase(
  days: Day[],
  weight: Weight[],
  goalProtein: number,
  rangeDays: number,
): { text: string; ask: string } | null {
  const withFood = days.filter((d) => d.calories > 0);

  // Меньше пяти дней с записями — выводы будут выдумкой, а не наблюдением.
  if (withFood.length < 5) return null;

  const avgCal = Math.round(withFood.reduce((s, d) => s + d.calories, 0) / withFood.length);
  const avgProt = Math.round(withFood.reduce((s, d) => s + d.protein, 0) / withFood.length);

  // Вес: сравниваем первый и последний замер в периоде
  const wDelta =
    weight.length > 1
      ? Number((weight[weight.length - 1].w - weight[0].w).toFixed(1))
      : null;

  // Самое ценное — связка «белок ↔ вес». Она есть только когда вес реально
  // движется и белка при этом хватает: иначе это совпадение, а не связь.
  if (wDelta !== null && wDelta <= -0.5 && goalProtein > 0 && avgProt >= goalProtein * 0.8) {
    return {
      text: `Минус ${Math.abs(wDelta).toFixed(1)} кг за ${rangeDays} дней — и белок ты держишь (${avgProt} г в среднем). Это и есть та связка, ради которой всё считается.`,
      ask: 'Разбери, за счёт чего у меня идёт снижение веса',
    };
  }

  if (wDelta !== null && wDelta <= -0.5) {
    return {
      text: `Минус ${Math.abs(wDelta).toFixed(1)} кг за ${rangeDays} дней. Белка в среднем ${avgProt} г — если добрать, уходить будет жир, а не мышцы.`,
      ask: 'Как добрать белок, чтобы уходил жир, а не мышцы?',
    };
  }

  if (wDelta !== null && wDelta >= 0.5) {
    return {
      text: `Вес прибавил ${wDelta.toFixed(1)} кг за ${rangeDays} дней при ${avgCal} ккал в среднем. Разберём, где утечка?`,
      ask: `Вес растёт при ${avgCal} ккал в день. В чём может быть дело?`,
    };
  }

  // Веса нет или он стоит — говорим о том, что видно: дисциплина записей
  const coverage = Math.round((withFood.length / rangeDays) * 100);
  if (coverage >= 80) {
    return {
      text: `Ты записал ${withFood.length} дней из ${rangeDays} — это ${coverage}%. По таким данным я уже могу говорить точно, а не гадать.`,
      ask: 'Разбери мои последние дни — что видно по еде?',
    };
  }

  if (goalProtein > 0 && avgProt < goalProtein * 0.7) {
    return {
      text: `Белка в среднем ${avgProt} г при норме ${goalProtein}. Это то, что стоит подтянуть раньше всего остального.`,
      ask: 'Чем добрать белок в течение дня?',
    };
  }

  return null;
}

export function StatsSlavaLine({
  days, weight, goalProtein, rangeDays,
}: {
  days: Day[];
  weight: Weight[];
  goalProtein: number;
  rangeDays: number;
}) {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const p = buildStatsPhrase(days, weight, goalProtein, rangeDays);
  if (!p) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      className="card mb-5 p-3 flex items-start gap-2.5"
      style={{ borderColor: 'rgba(16,229,142,0.28)' }}
    >
      <div className="w-[26px] h-[26px] rounded-full bg-emerald-glow/15 flex items-center justify-center shrink-0 mt-0.5">
        <Sparkles size={14} className="text-emerald-glow" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] leading-snug text-ink">{p.text}</div>
        <button
          onClick={() => {
            haptic('light');
            try { sessionStorage.setItem('slava_prefill', p.ask); } catch {}
            nav('/chat');
          }}
          className="mt-1.5 text-xs font-medium text-emerald-glow"
        >
          спросить Дофи →
        </button>
      </div>
    </motion.div>
  );
}
